<script setup>
// Тіло гілки коментарів: стрічка + поле написання.
//
// Раніше тут був список з comments.app і кнопка «Написати», що розгортала
// їхній фрейм із входом через Telegram. Тепер гілка своя (див. useThread і
// backend/src/api/thread): той самий формат, що й у чату ефіру — нік своїм
// кольором, значок, смайли 7TV, згадки, до пʼяти картинок під спойлером,
// відповіді, лайки, модерація, — а вхід той самий, що й на решті сайту:
// Twitch або Discord. Старі коментарі бекенд переносить у гілку сам.
//
// Пропси лишились колишні (pageId, pageTitle, pageUrl…), тож усі місця, де
// гілка вбудована — плеєр, плаваючий чат, блоки під серією, кліпи, — не
// знають, що всередині щось змінилось.

const props = defineProps({
  pageId: { type: String, required: true },
  // Лишились для сумісності: comments.app брав з них підписи для модерації.
  // Нашій гілці вони не потрібні
  pageTitle: { type: String, default: '' },
  pageUrl: { type: String, default: '' },
  interval: { type: Number, default: 10000 },
  limit: { type: Number, default: 30 },
  emptyText: {
    type: String,
    default: 'Повідомлень ще немає — напишіть перше'
  },
  // 'chat' — суцільна колонка чату, 'page' — блок на сторінці
  variant: { type: String, default: 'chat' },
  placeholder: { type: String, default: '' }
})

const {
  messages,
  hasMore,
  loading,
  loadingMore,
  failed,
  me,
  limits,
  notice,
  canModerate,
  muted,
  loadMore,
  send,
  remove,
  react,
  mute,
  uploadMedia
} = useThread(() => props.pageId, { interval: props.interval, limit: props.limit })

const listRef = ref(null)
const { stuck, missed, scrollToBottom, onScroll: onStickScroll, onAppend } = useStickyScroll(listRef)

// Нові знизу — тримаємось низу; старіші догорнули зверху — не рахуємо їх
// «новими» (там окрема обробка в fetchOlder)
let prepending = false
watch(messages, (next, prev) => {
  if (prepending) return
  onAppend(Math.max(0, next.length - (prev?.length || 0)))
})

const LOAD_MORE_ZONE = 60

const onScroll = () => {
  onStickScroll()
  const el = listRef.value
  if (el && el.scrollTop < LOAD_MORE_ZONE) fetchOlder()
}

// Догорнули вгору — доганяємо історію і повертаємо погляд на те саме
// повідомлення: інакше список стрибне на новододані зверху
const fetchOlder = async () => {
  const el = listRef.value
  if (!el || !hasMore.value || loadingMore.value) return

  const before = el.scrollHeight
  prepending = true
  try {
    await loadMore()
    await nextTick()
    if (listRef.value) listRef.value.scrollTop += listRef.value.scrollHeight - before
  } finally {
    prepending = false
  }
}

watch(
  () => props.pageId,
  () => {
    replyTo.value = null
    nextTick(scrollToBottom)
  }
)

// --- Відповіді й згадки ---
const replyTo = ref(null)
const composer = ref(null)

const startReply = (message) => {
  replyTo.value = {
    id: message.id,
    author: message.author,
    text: message.text || '',
    media: Array.isArray(message.media) ? message.media.length > 0 : !!message.media,
    deleted: !!message.deleted
  }
}

const onMention = (name) => composer.value?.mention(name)

const canReply = computed(() => !!me.value)

const chatters = computed(() => [
  ...new Set(messages.value.filter((m) => m.userId).map((m) => m.author))
])

const flashed = ref(null)
let flashTimer = null

const jumpTo = (id) => {
  const el = listRef.value?.querySelector(`#chat-msg-${CSS.escape(String(id))}`)
  if (!el) {
    notice.value = 'Того коментаря вже немає в стрічці — догорніть вище'
    return
  }
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  flashed.value = id
  if (flashTimer) clearTimeout(flashTimer)
  flashTimer = setTimeout(() => {
    flashed.value = null
  }, 2000)
}

// Помилку показуємо кілька секунд: вона про останню дію
let noticeTimer = null
watch(notice, (value) => {
  if (noticeTimer) clearTimeout(noticeTimer)
  if (!value) return
  noticeTimer = setTimeout(() => {
    notice.value = ''
  }, 4000)
})

onBeforeUnmount(() => {
  if (noticeTimer) clearTimeout(noticeTimer)
  if (flashTimer) clearTimeout(flashTimer)
})

// Надіслали своє — лишаємось унизу, навіть якщо до того читали старе
const onSend = async (payload) => {
  const ok = await send(payload)
  nextTick(scrollToBottom)
  return ok
}

// Свій нік, колір і значок — той самий екран, що в чаті ефіру: профіль
// спільний на весь сайт
const settingsOpen = ref(false)
const onProfileSaved = (profile) => {
  me.value = { ...me.value, ...profile }
}
</script>

<template>
  <!-- min-w-0 w-full: у плаваючому вікні стрічка лежить у flex-рядку, і
       без цього розтягувалась ширше за вікно — рядок «Увійдіть…» з кнопками
       центрувався по ширшому блоку й обрізався -->
  <div
    class="relative flex flex-col flex-1 w-full min-w-0 min-h-0"
    :class="{ 'bg-base-300': variant !== 'page' }"
  >
    <div
      ref="listRef"
      class="flex flex-col gap-0.5 p-1.5 min-h-0 overflow-y-auto grow"
      @scroll.passive="onScroll"
    >
      <div
        v-if="loading && !messages.length"
        class="flex flex-col gap-2"
      >
        <div
          v-for="n in 4"
          :key="n"
          class="bg-base-200 rounded h-10 animate-pulse"
        />
      </div>

      <p
        v-else-if="failed && !messages.length"
        class="my-auto text-center text-base-content/50 text-xs"
      >
        Не вдалося завантажити повідомлення
      </p>

      <p
        v-else-if="!messages.length"
        class="my-auto py-6 text-center text-base-content/50 text-xs"
      >
        {{ emptyText }}
      </p>

      <button
        v-if="hasMore && messages.length"
        type="button"
        class="py-1 text-[11px] text-base-content/40 hover:text-primary text-center"
        :disabled="loadingMore"
        @click="fetchOlder"
      >
        {{ loadingMore ? 'Завантажуємо старіші…' : 'Показати старіші' }}
      </button>

      <ChatThreadMessage
        v-for="message in messages"
        :key="message.id"
        :message="message"
        :me-id="me?.id || null"
        :me-name="me?.name || ''"
        :can-moderate="canModerate"
        :can-reply="canReply"
        :flash="flashed === message.id"
        @delete="remove"
        @mute="({ userId, seconds }) => mute(userId, seconds)"
        @reply="startReply"
        @react="react"
        @mention="onMention"
        @jump="jumpTo"
      />
    </div>

    <button
      v-if="!stuck && messages.length"
      class="bottom-[64px] left-1/2 z-20 absolute bg-primary/90 hover:bg-primary shadow px-3 py-1 rounded-full text-[11px] text-primary-content -translate-x-1/2"
      @click="scrollToBottom"
    >
      {{ missed ? `Нових: ${missed}` : 'Вниз' }}
    </button>

    <p
      v-if="notice"
      class="flex-shrink-0 bg-error/15 px-2 py-1 text-[11px] text-error text-center"
    >
      {{ notice }}
    </p>

    <div class="relative">
      <button
        v-if="me"
        type="button"
        class="-top-6 right-1.5 z-10 absolute flex items-center gap-1 bg-base-200/80 px-1.5 py-0.5 rounded text-[10px] text-base-content/50 hover:text-primary"
        title="Мій нік, колір і значок"
        @click="settingsOpen = true"
      >
        <span
          class="font-semibold"
          :style="{ color: me.color || nickColor(me.name) }"
          >{{ me.name }}</span
        >
        ⚙
      </button>

      <ChatLiveComposer
        ref="composer"
        :send="onSend"
        :upload="uploadMedia"
        :authenticated="!!me"
        :muted="muted"
        :muted-until="me?.mutedUntil || null"
        :connected="!loading || !!messages.length"
        :max-length="limits.maxText"
        :max-media="limits.maxMedia"
        :chatters="chatters"
        :me-name="me?.name || ''"
        :reply-to="replyTo"
        :global-hotkey="false"
        :placeholder="placeholder || (variant === 'page' ? 'Написати коментар…' : 'Написати в чат…')"
        @cancel-reply="replyTo = null"
      />
    </div>

    <ChatLiveSettings
      v-if="settingsOpen && me"
      :me="me"
      @saved="onProfileSaved"
      @close="settingsOpen = false"
    />
  </div>
</template>
